import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection, IssueFieldSelectionValues} from '@github-ui/issue-fields-shared/issue-field'
import {
  asIssueFieldSelection,
  getIssueFieldValue,
  parsePinnedIssueFields,
  toIssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {useIssueFieldsAnalytics} from '@github-ui/issue-fields-shared/use-issue-fields-analytics'
import {IssuesLoadingSkeleton} from '@github-ui/issues-loading-skeleton'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {noop} from '@github-ui/noop'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Heading, Tooltip} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {
  areFieldValuesEqual,
  createClearedFieldValue,
  getFieldChanges,
  getIssueFieldSelectionValue,
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
  toIssueFieldWithValue,
} from '../../utils/issue-field-helper'
import {Section} from '../Section'
import type {FieldsSectionFieldValues$key, IssueFieldDataType} from './__generated__/FieldsSectionFieldValues.graphql'
import type {FieldsSectionFragment$key} from './__generated__/FieldsSectionFragment.graphql'
import {AddIssueFieldButton} from './AddIssueFieldButton'
import {LABELS} from './constants/labels'
import {FieldsSelector} from './FieldSelector'
import styles from './FieldsSection.module.css'
import {
  getPinnedFieldClassName,
  getPinnedFieldPosition,
  runFieldMutation,
  usePendingFieldFocus,
  usePinnedFieldsAnimation,
} from './helpers'
import {usePinnedFieldsState} from './hooks/use-pinned-fields-state'
import {ErrorDialog, getEditor, getIssueFieldEditor, getPendingEditor, type SafeFieldValue} from './IssueFieldEditors'
import {commitCreateIssueFieldValueMutation} from './mutations/create-issue-field-value-mutation'
import {commitDeleteIssueFieldValueMutation} from './mutations/delete-issue-field-value-mutation'
import {commitUpdateIssueFieldValueMutation} from './mutations/update-issue-field-value-mutation'

export type CreateIssueFieldsSectionProps = {
  owner: string
  ownerId?: number | null
  onSelectionChange: (fields: IssueFieldWithUnsavedValue[]) => void
  issueType?: IssueTypePickerIssueType | null
  orgPinnedFields?: readonly IssueFieldSelection[] | null
  /** When true, hides the "no pinned fields" message because pinned fields are still loading */
  isPinnedFieldsLoading?: boolean
}

export function CreateIssueFieldsSection({
  owner,
  ownerId,
  onSelectionChange,
  issueType,
  orgPinnedFields,
  isPinnedFieldsLoading,
}: CreateIssueFieldsSectionProps) {
  const [fields, setFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [pendingField, setPendingField] = useState<IssueFieldSelection | null>(null)
  const {animatePinned} = usePinnedFieldsAnimation(issueType?.name || null)

  const {markInteractionStart, reportUsage} = useIssueFieldsAnalytics()
  const {setAddFieldButtonElement} = usePendingFieldFocus(pendingField)

  // Use issue type pinned fields if available, otherwise org pinned fields
  const mergedPinnedFields: readonly IssueFieldSelection[] = useMemo(
    () =>
      parsePinnedIssueFields(issueType?.pinnedFields?.filter(f => f?.__typename !== '%other') ?? []) ??
      orgPinnedFields ??
      [],
    [issueType, orgPinnedFields],
  )

  const {pinnedFieldIds} = usePinnedFieldsState({
    pinnedFields: mergedPinnedFields,
    setFields,
    setFieldsSet,
  })

  const hideAddFieldButton = !isFeatureEnabled('issue_metadata_add_field_button')

  useEffect(() => {
    const fieldsToReport = fields.filter(field => {
      // Only include fields that have actual values
      return field.value
    })
    onSelectionChange(fieldsToReport)
  }, [fields, onSelectionChange])

  // Ensure fieldsSet is synchronized with all visible fields to prevent dropdown reappearance
  useEffect(() => {
    const allVisibleFieldNames = fields.map(f => f.field.name).filter((name): name is string => !!name)
    const allFieldNames = [...new Set([...allVisibleFieldNames, ...fieldsSet])]

    // Only update if there are actual differences
    if (allFieldNames.length !== fieldsSet.length || !allFieldNames.every(name => fieldsSet.includes(name))) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFieldsSet(allFieldNames)
    }
  }, [fields, fieldsSet])

  const onIssueFieldSelected = useCallback(
    (selectedField: IssueFieldSelection) => {
      markInteractionStart()

      if (!selectedField.id) return
      setPendingField(selectedField)

      if (selectedField.name) {
        setFieldsSet(prev => [...prev, selectedField.name])
      }
    },
    [markInteractionStart],
  )

  const onCommit = useCallback(
    (fieldId: string, value: IssueFieldSelectionValues) => {
      setFields(prevFields => {
        let committedField: IssueFieldWithUnsavedValue | undefined
        const updatedFields = prevFields
          // update new value
          .map(field => {
            if (field.field.id === fieldId) {
              committedField = toIssueFieldWithValue(field.field, value)
              return committedField
            }
            return field
          })
          // Keep all selected fields in state for UI (even empty), but only persist pinned fields with empty values
          .filter(f => f.value !== undefined || (f.field.id && pinnedFieldIds.has(f.field.id)))

        // remove from fieldsSet only if field is completely removed (not kept as pinned field)
        if (committedField && !committedField.value) {
          const isPinnedField = committedField.field.id && pinnedFieldIds.has(committedField.field.id)
          if (!isPinnedField) {
            setFieldsSet(prev => prev.filter(name => name !== committedField?.field.name))
          }
        }

        if (committedField && committedField.field.dataType) {
          reportUsage({
            page: 'issue#new',
            action: 'create',
            fieldId,
            fieldType: toIssueFieldType(committedField.field.dataType),
            status: 'success',
            orgId: ownerId?.toString(),
          })
        }

        return updatedFields
      })
    },
    [ownerId, pinnedFieldIds, reportUsage],
  )

  const onPendingFieldCommit = useCallback(
    (fieldId: string, fieldValue: IssueFieldSelectionValues) => {
      if (!pendingField || pendingField.id !== fieldId) return

      // Remove from pending state
      setPendingField(null)

      const fieldType = toIssueFieldType(pendingField.dataType)
      const value = getIssueFieldSelectionValue(fieldType, fieldValue)
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // Remove from fieldsSet if field is being cancelled/removed
      if (pendingField.name && isEmpty) {
        setFieldsSet(prev => prev.filter(name => name !== pendingField.name))
        return
      }

      // If value is empty, don't save the value (field is being removed)
      if (isEmpty) {
        return
      }

      // Commit the field value
      setFields(prevFields => {
        const newFields = [...prevFields]
        if (pendingField.name && !newFields.some(f => f.field.name === pendingField.name)) {
          newFields.push(toIssueFieldWithUnsavedValue(pendingField, fieldValue))
        }
        return newFields
      })

      // Ensure the field name is in fieldsSet when successfully committed
      if (pendingField.name) {
        setFieldsSet(prev => (prev.includes(pendingField.name) ? prev : [...prev, pendingField.name]))
      }
    },
    [pendingField],
  )

  const isCompactViewEnabled = isFeatureEnabled('issue_fields_compact_view')

  // Get all field IDs in order for position calculation
  const allFieldIds = fields.map(f => f.field.id).filter((id): id is string => !!id)

  const fieldEditors = fields.map(field => {
    if (!field.field.id) return null

    const fieldValue = typeof field.value === 'object' && field.value !== null ? {...field, ...field.value} : field
    const isPinned = pinnedFieldIds.has(field.field.id)
    const position = getPinnedFieldPosition(field.field.id, pinnedFieldIds, allFieldIds)
    return getEditor({
      fieldValue: fieldValue as SafeFieldValue,
      readonly: false,
      onCommit,
      className: getPinnedFieldClassName(isPinned, animatePinned, position),
    })
  })

  const pendingFieldEditor = pendingField
    ? (() => {
        const isPinned = pinnedFieldIds.has(pendingField.id)
        // Pending field is always added at the end
        const allIdsWithPending = pendingField.id ? [...allFieldIds, pendingField.id] : allFieldIds
        const position = pendingField.id
          ? getPinnedFieldPosition(pendingField.id, pinnedFieldIds, allIdsWithPending)
          : null
        return getPendingEditor({
          fieldSelection: pendingField,
          readonly: false,
          onCommit: onPendingFieldCommit,
          className: getPinnedFieldClassName(isPinned, animatePinned, position),
        })
      })()
    : null

  const fieldSelector = (
    <FieldsSelector
      owner={owner}
      fieldsSet={fieldsSet}
      onIssueFieldSelected={onIssueFieldSelected}
      markInteractionStart={markInteractionStart}
      disabled={pendingField !== null}
      onButtonElementReceived={setAddFieldButtonElement}
      hideAddFieldButton={hideAddFieldButton}
    />
  )

  const visibleFieldEditors = fieldEditors.filter(Boolean)

  // Show skeleton when loading or when pinned fields data exists but hasn't rendered yet
  const isWaitingForFieldsToRender = mergedPinnedFields.length > 0 && visibleFieldEditors.length === 0
  const showNoPinnedFieldsMessage = !isPinnedFieldsLoading && !isWaitingForFieldsToRender

  if (isCompactViewEnabled) {
    return (
      <>
        <Section
          sectionHeader={
            <Heading as="h3" className={styles.compactSectionHeader}>
              Fields
            </Heading>
          }
        >
          {visibleFieldEditors.length > 0 ? (
            <>{visibleFieldEditors}</>
          ) : showNoPinnedFieldsMessage ? (
            <span className={styles.compactSectionEmptyText}>{LABELS.noPinnedFieldsMessage(issueType?.name)}</span>
          ) : (
            <>
              <span className={styles.loadingRow}>
                <IssuesLoadingSkeleton height="md" width="40%" />
                <IssuesLoadingSkeleton height="md" width="60%" />
              </span>
            </>
          )}
        </Section>
        {pendingFieldEditor}
        {fieldSelector}
      </>
    )
  }

  return (
    <>
      {fieldEditors}
      {pendingFieldEditor}
      {fieldSelector}
    </>
  )
}

export type EditIssueFieldsSectionProps = {
  onUpdate: () => void
  issue: FieldsSectionFragment$key
  inProjectsPage?: boolean
  readonly: boolean
}

export function EditIssueFieldsSection({issue, onUpdate, inProjectsPage, readonly}: EditIssueFieldsSectionProps) {
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [mutationErrorPresent, setMutationErrorPresent] = useState<boolean>(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false)
  const [pendingField, setPendingField] = useState<IssueFieldSelection | null>(null)
  const environment = useRelayEnvironment()
  const {markInteractionStart, reportUsage} = useIssueFieldsAnalytics()
  const {setAddFieldButtonElement} = usePendingFieldFocus(pendingField)

  const hideAddFieldButton = !isFeatureEnabled('issue_metadata_add_field_button')

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment FieldsSectionFragment on Issue {
        id
        repository {
          isPrivate
          owner {
            login
            databaseId
            ... on Organization {
              issueFieldsEnabled: isFeatureEnabled(name: "issue_fields")
              pinnedIssueFields(first: 100) {
                nodes {
                  __typename
                  ... on IssueFieldText {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldDate {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    id
                    name
                    dataType
                  }
                }
              }
            }
          }
        }
        viewerCanSetFields
        ...FieldsSectionFieldValues
        issueType {
          id
          name
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
          }
        }
      }
    `,
    issue,
  )

  const {
    repository: {
      isPrivate,
      owner: {login: owner},
    },
    viewerCanSetFields,
    issueType,
  } = data

  const issueFieldsEnabled = data.repository.owner.issueFieldsEnabled ?? false

  const orgPinnedFields: readonly IssueFieldSelection[] | null = useMemo(() => {
    return parsePinnedIssueFields(
      data.repository.owner.pinnedIssueFields?.nodes?.filter(node => node?.__typename !== '%other') ?? [],
    )
  }, [data.repository.owner.pinnedIssueFields])

  // Use issue type pinned fields if available, otherwise org pinned fields
  const mergedPinnedFields: readonly IssueFieldSelection[] = useMemo(
    () =>
      parsePinnedIssueFields(issueType?.pinnedFields?.filter(f => f?.__typename !== '%other')) ?? orgPinnedFields ?? [],
    [issueType, orgPinnedFields],
  )

  const pinnedFields = useMemo(() => {
    return mergedPinnedFields.map(field => toIssueFieldWithUnsavedValue(asIssueFieldSelection(field), null))
  }, [mergedPinnedFields])

  const handleErrors = useCallback(() => {
    setMutationErrorPresent(true)
  }, [])

  const onCommitPendingField = useCallback(
    (fieldId: string, payload: string | null) => {
      if (!pendingField || pendingField.id !== fieldId) return

      // Remove from pending state
      setPendingField(null)

      const value = getIssueFieldSelectionValue(pendingField.dataType, payload || '')
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // Remove from fieldsSet if field is being cancelled/removed
      if (pendingField.name && isEmpty) {
        setFieldsSet(prev => prev.filter(name => name !== pendingField.name))
        return
      }

      // If value is empty, don't make API call (field is being removed)
      if (isEmpty) {
        return
      }

      // Make API call to create the field with the committed value
      runFieldMutation({
        mutationFn: commitCreateIssueFieldValueMutation,
        input: {
          issueId: data.id,
          issueField: {
            fieldId,
            ...getIssueFieldValue(pendingField.dataType, value),
          },
        },
        environment,
        action: 'create',
        fieldId,
        fieldType: toIssueFieldType(pendingField.dataType),
        inProjectsPage,
        ownerId: data.repository?.owner?.databaseId,
        reportUsage,
        onError: handleErrors,
      })
    },
    [pendingField, data.id, data.repository.owner.databaseId, environment, inProjectsPage, reportUsage, handleErrors],
  )

  const onIssueFieldSelected = (selectedField: IssueFieldSelection) => {
    if (!selectedField.id) return
    setPendingField(selectedField)

    // Add to fieldsSet to prevent it from showing in selector again
    if (selectedField.name) {
      setFieldsSet(prev => [...prev, selectedField.name])
    }
  }

  if (!isPrivate || !issueFieldsEnabled) {
    return null
  }

  return (
    <>
      <ErrorBoundary
        fallback={
          <div>
            <Tooltip text={LABELS.loadingFieldsTooltipMessage}>
              <span>
                <AddIssueFieldButton inactive anchorProps={{onClick: () => setErrorDialogOpen(true)}} onClick={noop} />
              </span>
            </Tooltip>
            {errorDialogOpen && (
              <ErrorDialog onClose={() => setErrorDialogOpen(false)} message={LABELS.loadingFieldsErrorMessage} />
            )}
          </div>
        }
      >
        <IssueFields
          issue={data}
          fieldsSet={fieldsSet}
          setFieldsSet={setFieldsSet}
          inProjectsPage={inProjectsPage}
          pinnedFields={pinnedFields}
          readonly={readonly}
          onUpdate={onUpdate}
        />

        {/* Render pending field */}
        {pendingField &&
          (() => {
            const isPinned = pinnedFields.some(
              (field: IssueFieldWithUnsavedValue) => field.field.id === pendingField.id,
            )
            return getPendingEditor({
              fieldSelection: pendingField,
              readonly: false,
              onCommit: onCommitPendingField,
              className: isPinned ? styles.pinnedField : undefined,
            })
          })()}
        {!readonly && viewerCanSetFields && (
          <FieldsSelector
            owner={owner}
            fieldsSet={fieldsSet}
            onIssueFieldSelected={onIssueFieldSelected}
            markInteractionStart={markInteractionStart}
            disabled={pendingField !== null}
            onButtonElementReceived={setAddFieldButtonElement}
            hideAddFieldButton={hideAddFieldButton}
          />
        )}
      </ErrorBoundary>

      {mutationErrorPresent && (
        <ErrorDialog onClose={() => setMutationErrorPresent(false)} message={LABELS.mutationErrorMessage} />
      )}
    </>
  )
}

function IssueFields({
  issue,
  fieldsSet,
  setFieldsSet,
  inProjectsPage,
  onUpdate,
  pinnedFields,
  readonly,
}: {
  issue: FieldsSectionFieldValues$key
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
  inProjectsPage?: boolean
  pinnedFields?: IssueFieldWithUnsavedValue[]
  readonly?: boolean
  onUpdate: () => void
}) {
  const environment = useRelayEnvironment()
  const [errorPresent, setErrorPresent] = useState<Record<string, boolean>>({})
  const {reportUsage} = useIssueFieldsAnalytics()

  // Add beforeunload event handler for field-level errors
  useEffect(() => {
    const hasAnyErrors = Object.values(errorPresent).some(isError => isError)

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasAnyErrors) {
        event.preventDefault()
        // This message will be displayed by browsers when users try to reload the page
        event.returnValue = LABELS.reloadWarningMessage
        return event.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [errorPresent])
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment FieldsSectionFieldValues on Issue {
        id
        issueFieldValues(first: 25) {
          nodes {
            ... on IssueFieldTextValue {
              field {
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldSingleSelectValue {
              field {
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
              }
              name
              # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
              color
              # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
              description
            }
            ... on IssueFieldDateValue {
              field {
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldNumberValue {
              field {
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                }
              }
              # eslint-disable-next-line relay/unused-fields
              numberValue: value
            }
          }
        }
        viewerCanSetFields
        issueType {
          id
          name
        }
      }
    `,
    issue,
  )

  // Use issue type ID for animation instead of pinned field IDs
  // This ensures ALL pinned fields animate when issue type changes, not just newly added ones
  const issueTypeId = data.issueType?.id || null
  const {animatePinned} = usePinnedFieldsAnimation(issueTypeId)

  const filteredFieldValues: SafeFieldValue[] = (data.issueFieldValues?.nodes || []).map(fv => fv as SafeFieldValue)

  const [effectiveFieldValues, setEffectiveFieldValues] = useState<SafeFieldValue[]>(
    filteredFieldValues.filter(fv => fv?.field != null),
  )

  // Reset effectiveFieldValues when navigating to a different issue
  useEffect(() => {
    const newFilteredFieldValues: SafeFieldValue[] = (data.issueFieldValues?.nodes || []).map(
      fv => fv as SafeFieldValue,
    )
    setEffectiveFieldValues(newFilteredFieldValues.filter(fv => fv?.field != null))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only reset when issue ID changes, not on every field update
  }, [data.id])

  const handleErrors = useCallback((fieldId: string) => {
    setErrorPresent(prev => ({...prev, [fieldId]: true}))
  }, [])

  const onCommitField = useCallback(
    (fieldId: string, fieldType: IssueFieldDataType, value: IssueFieldSelectionValues) => {
      const issueFieldType = toIssueFieldType(fieldType)
      const selectionValue = getIssueFieldSelectionValue(issueFieldType, value)
      if (value === '' || value === null) {
        runFieldMutation({
          mutationFn: commitDeleteIssueFieldValueMutation,
          input: {
            issueId: data.id,
            fieldId,
          },
          environment,
          action: 'delete',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onCompleted: () => {
            setErrorPresent(prev => ({...prev, [fieldId]: false}))
            onUpdate()
          },
          onError: () => handleErrors(fieldId),
        })
      } else {
        runFieldMutation({
          mutationFn: commitUpdateIssueFieldValueMutation,
          input: {
            issueId: data.id,
            issueField: {
              fieldId,
              ...getIssueFieldValue(issueFieldType, selectionValue),
            },
          },
          environment,
          action: 'update',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onCompleted: () => {
            setErrorPresent(prev => ({...prev, [fieldId]: false}))
            onUpdate()
          },
          onError: () => handleErrors(fieldId),
        })
      }
    },
    [data.id, environment, handleErrors, inProjectsPage, reportUsage, onUpdate],
  )

  useMemo(() => {
    const {addedFields, updatedFields, removedFields} = getFieldChanges(effectiveFieldValues, filteredFieldValues)

    if (addedFields.length === 0 && updatedFields.length === 0 && removedFields.length === 0) {
      return
    }

    let shouldSet = addedFields.length > 0

    // We want to maintain the initial order of fields, so we iterate over effectiveFieldValues
    const updatedFieldValues = effectiveFieldValues
      .map(fieldValue => {
        const updatedField = updatedFields.find(uf => uf?.field?.id === fieldValue?.field?.id)
        if (updatedField) {
          if (!areFieldValuesEqual(updatedField, fieldValue)) shouldSet = true
          return updatedField
        }

        // Handle field removals: clear value for all fields
        const isRemoved = removedFields.some(
          (removedField: (typeof filteredFieldValues)[number]) => removedField?.field?.id === fieldValue?.field?.id,
        )
        if (isRemoved) {
          const clearedField = createClearedFieldValue(fieldValue)
          if (!areFieldValuesEqual(clearedField, fieldValue)) shouldSet = true
          return clearedField
        }

        return fieldValue
      })
      .filter(fv => fv !== null && fv !== undefined)

    if (shouldSet) setEffectiveFieldValues([...updatedFieldValues, ...addedFields])
  }, [filteredFieldValues, effectiveFieldValues])

  // Merge pinned fields at the beginning, then existing fields
  const allFields = useMemo(() => {
    if (!pinnedFields || pinnedFields.length === 0) {
      return effectiveFieldValues
    }

    // If any of the pinned fields already exists as a user-set field with a value, we use it and its existing value, otherwise we use the default pinned field
    const newPinnedFields = pinnedFields.map(pinnedField => {
      const userSetField = effectiveFieldValues.find(fieldValue => fieldValue.field.id === pinnedField.field.id)
      return userSetField ? userSetField : pinnedField
    })

    // remove the user-set fields that are now pinned and move unpinned fields with values to the user-set list
    const pinnedIds = newPinnedFields.map(pinnedField => pinnedField.field.id)
    const userSetFields = effectiveFieldValues.filter(fv => !pinnedIds.includes(fv.field.id))

    return [...newPinnedFields, ...userSetFields]
  }, [pinnedFields, effectiveFieldValues])

  // Update fieldsSet to include all visible fields (both with values and cleared fields)
  // to prevent them from appearing in the dropdown picker
  useEffect(() => {
    const allVisibleFieldNames = allFields.map(fv => fv?.field?.name || '').filter(name => name)
    const arraysAreEqual =
      fieldsSet.length === allVisibleFieldNames.length &&
      fieldsSet.every((name, idx) => name === allVisibleFieldNames[idx])
    if (!arraysAreEqual) {
      setFieldsSet(allVisibleFieldNames)
    }
  }, [fieldsSet, allFields, setFieldsSet])

  // Get all field IDs in order for position calculation
  const allFieldIds = allFields.map(fv => fv?.field?.id).filter((id): id is string => !!id)
  // Build a Set of pinned field IDs for position calculation
  const pinnedFieldIdSet = new Set(pinnedFields?.map(pf => pf.field.id).filter((id): id is string => !!id) ?? [])

  const fieldValues = allFields.map(fieldValue => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    if (fieldValue.field === undefined || fieldValue.field.dataType === undefined) return null

    // Check if this field is pinned
    const isPinned = pinnedFields?.some(pf => pf.field.id === fieldValue.field.id) || false
    const readonlyField = readonly || !data.viewerCanSetFields
    const position = getPinnedFieldPosition(fieldValue.field.id, pinnedFieldIdSet, allFieldIds)

    return getIssueFieldEditor({
      errorPresent,
      setErrorPresent: (fieldId: string, present: boolean) => setErrorPresent(prev => ({...prev, [fieldId]: present})),
      // We need the casting because the field.id property can be undefined in GraphQL
      fieldValue: fieldValue as SafeFieldValue,
      readonly: readonlyField,
      onCommit: (fieldId: string, value: IssueFieldSelectionValues) =>
        onCommitField(fieldId, fieldValue.field.dataType!, value),
      className: getPinnedFieldClassName(isPinned, animatePinned, position),
    })
  })

  const visibleFieldValues = fieldValues.filter(Boolean)
  const isCompactViewEnabled = isFeatureEnabled('issue_fields_compact_view')

  return isCompactViewEnabled ? (
    <Section
      sectionHeader={
        <Heading as="h3" className={styles.compactSectionHeader}>
          Fields
        </Heading>
      }
    >
      {visibleFieldValues.length > 0 ? (
        <>{visibleFieldValues}</>
      ) : (
        <span className={styles.compactSectionEmptyText}>{LABELS.noPinnedFieldsMessage(data.issueType?.name)}</span>
      )}
    </Section>
  ) : (
    <>{visibleFieldValues}</>
  )
}

try{ CreateIssueFieldsSection.displayName ||= 'CreateIssueFieldsSection' } catch {}
try{ EditIssueFieldsSection.displayName ||= 'EditIssueFieldsSection' } catch {}
try{ IssueFields.displayName ||= 'IssueFields' } catch {}